import React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import {
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material'
import { ArrowForwardRounded } from '@mui/icons-material'
import { StudentRatingState } from '../../../../features/types'
import { tableStyles, ui } from '../../../../utils/uiTokens'

interface Props {
    students: StudentRatingState[]
    monthNumber?: number
    weekNumber?: number
}

// Первые три места - как на подиуме: золото, серебро, бронза
const rankColors = ['#F0A22E', '#98A2B3', '#B4713A']

const styles = {
    rank: (index: number) => ({
        width: 28,
        height: 28,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '13px',
        fontWeight: 600,
        color: index < 3 ? '#fff' : ui.sub,
        backgroundColor: index < 3 ? rankColors[index] : '#F1EEF2',
    }),
    avatar: {
        width: 34,
        height: 34,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12.5px',
        fontWeight: 600,
        color: ui.pink,
        backgroundColor: ui.pinkSoft,
        flexShrink: 0,
    },
    name: {
        fontSize: '14px',
        fontWeight: 500,
        color: ui.ink,
    },
    group: {
        fontSize: '12px',
        color: ui.muted,
    },
    value: {
        fontSize: '15px',
        fontWeight: 600,
        color: ui.ink,
    },
    hint: {
        fontSize: '11.5px',
        color: ui.muted,
    },
    chip: (tone: 'done' | 'partial' | 'none') => ({
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '100px',
        fontSize: '13px',
        fontWeight: 600,
        color:
            tone === 'done' ? ui.green : tone === 'partial' ? '#B26B00' : ui.muted,
        backgroundColor:
            tone === 'done' ? '#E8F6EE' : tone === 'partial' ? '#FDF3E4' : '#F5F3F6',
    }),
    total: {
        fontSize: '17px',
        fontWeight: 700,
        color: ui.ink,
    },
}

const RatingTable: React.FC<Props> = ({
    students = [],
    monthNumber,
    weekNumber,
}) => {
    const { formatMessage } = useIntl()

    const initials = (student: StudentRatingState) =>
        `${student.firstName?.[0] || ''}${student.lastName?.[0] || ''}`.toUpperCase()

    const tone = (value: number, max: number) => {
        if (!max || !value) return 'none' as const
        return value >= max ? ('done' as const) : ('partial' as const)
    }

    const reportLink = (student: StudentRatingState) => {
        const params = new URLSearchParams()
        if (monthNumber) params.set('monthNumber', String(monthNumber))
        if (weekNumber) params.set('weekNumber', String(weekNumber))

        return `/curator/rating/${student.userSubjectEnrollId}?${params.toString()}`
    }

    return (
        <TableContainer
            component={Paper}
            sx={tableStyles.container}
            elevation={0}
        >
            <Table>
                <TableHead sx={tableStyles.head}>
                    <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>{formatMessage({ id: 'student' })}</TableCell>
                        <TableCell align="center">{formatMessage({ id: 'video' })}</TableCell>
                        <TableCell align="center">{formatMessage({ id: 'test' })}</TableCell>
                        <TableCell align="center">{formatMessage({ id: 'conspect' })}</TableCell>
                        <TableCell align="center">{formatMessage({ id: 'zachet' })}</TableCell>
                        <TableCell align="center">{formatMessage({ id: 'practice' })}</TableCell>
                        <TableCell align="center">{formatMessage({ id: 'totalPoint' })}</TableCell>
                        <TableCell align="right">{formatMessage({ id: 'details' })}</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {students.length ? (
                        students.map((student, index) => (
                            <TableRow
                                key={student.userSubjectEnrollId}
                                sx={tableStyles.row}
                            >
                                <TableCell>
                                    <Box sx={styles.rank(index)}>{index + 1}</Box>
                                </TableCell>

                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={styles.avatar}>{initials(student)}</Box>
                                        <Box>
                                            <Box sx={styles.name}>
                                                {student.firstName} {student.lastName}
                                            </Box>
                                            <Box sx={styles.group}>{student.groupTitle}</Box>
                                        </Box>
                                    </Box>
                                </TableCell>

                                <TableCell align="center">
                                    <Box sx={styles.value}>{student.videoPoint}</Box>
                                    <Box sx={styles.hint}>
                                        {student.videoDone}/{student.videoTotal} · 90%+
                                    </Box>
                                </TableCell>

                                <TableCell align="center">
                                    <Box sx={styles.value}>{student.testPoint}</Box>
                                    <Box sx={styles.hint}>{student.testPercent}%</Box>
                                </TableCell>

                                <TableCell align="center">
                                    <Box sx={styles.chip(tone(student.conspectPoint, student.conspectMax))}>
                                        {student.conspectPoint} / {student.conspectMax}
                                    </Box>
                                </TableCell>

                                <TableCell align="center">
                                    <Box sx={styles.chip(student.zachetPoint ? 'partial' : 'none')}>
                                        {student.zachetPoint}
                                    </Box>
                                </TableCell>

                                <TableCell align="center">
                                    <Box sx={styles.chip(student.practicePoint ? 'done' : 'none')}>
                                        {student.practicePoint}
                                    </Box>
                                </TableCell>

                                <TableCell align="center">
                                    <Box sx={styles.total}>{student.totalPoint}</Box>
                                    <Box sx={styles.hint}>/ {student.maxPoint}</Box>
                                </TableCell>

                                <TableCell align="right">
                                    <Link to={reportLink(student)}>
                                        <IconButton sx={{ background: ui.pinkSoft, borderRadius: '10px' }}>
                                            <ArrowForwardRounded sx={{ color: ui.pink, fontSize: 18 }} />
                                        </IconButton>
                                    </Link>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell
                                align="center"
                                colSpan={9}
                            >
                                {formatMessage({ id: 'noData' })}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>

            </Table>
        </TableContainer>
    )
}

export default RatingTable
